import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// PAYSLIP REVIEW STATUS UI
export default function MyPayslips() {
  const [payslips, setPayslips] = useState([]);
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [viewEntry, setViewEntry] = useState(null);

  useEffect(() => {
    get('/payroll/my-payslips').then(d => {
      setPayslips(d.data||[]);
      setStaff(d.staff||null);
      if (d.message) setMessage(d.message);
    }).finally(() => setLoading(false));
  }, []);

  const loadEntry = (id) => {
    get('/payroll/my-payslips/'+id).then(setViewEntry);
  };

  if (loading) return <div className="spinner"></div>;

  if (!staff) return (
    <div className="empty-state">
      <h3>No Payslip Record Found</h3>
      <p>Your account is not yet linked to a staff payroll record. Please contact the admin.</p>
    </div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">My Payslips</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Your payroll history — {staff.name}</p>
        </div>
      </div>

      {/* Staff info card */}
      <div style={{background:'#556B2F',borderRadius:10,padding:'16px 22px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:'#A8893D'}}>{staff.name}</div>
          <div style={{fontSize:12,color:'rgba(242,239,231,.6)',marginTop:3}}>
            {staff.role||''} {staff.department?'| '+staff.department:''}
          </div>
          {staff.bank_name && (
            <div style={{fontSize:11,color:'rgba(242,239,231,.4)',marginTop:3}}>
              {staff.bank_name} | {staff.bank_account_no}
            </div>
          )}
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:10,color:'rgba(242,239,231,.4)',textTransform:'uppercase',letterSpacing:'.08em'}}>Pay Type</div>
          <div style={{fontSize:13,color:'#F2EFE7',fontWeight:500,marginTop:2,textTransform:'capitalize'}}>{staff.pay_type}</div>
        </div>
      </div>

      {payslips.length === 0 ? (
        <div className="empty-state">
          <h3>No payslips available</h3>
          <p>
            Your payslips will appear here when they
            are published for review or marked as paid.
          </p>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Period</th>
                <th style={{textAlign:'right'}}>Base Pay</th>
                <th style={{textAlign:'right'}}>Gross Pay</th>
                <th style={{textAlign:'right'}}>Deductions</th>
                <th style={{textAlign:'right'}}>Net Pay</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payslips.map(p => (
                <tr key={p.id}>
                  <td style={{fontWeight:500}}>{MONTHS[(p.month||1)-1]} {p.year}</td>
                  <td className="amt">{fmt(p.base_pay)}</td>
                  <td className="amt">{fmt(p.gross_pay)}</td>
                  <td className="amt" style={{color:'var(--unpaid)'}}>{fmt(p.total_deductions)}</td>
                  <td className="amt" style={{color:'var(--paid)',fontWeight:700}}>{fmt(p.net_pay)}</td>
                  <td>
                    <span
                      className={
                        'badge ' +
                        (
                          p.payment_status ===
                            'paid'
                            ? 'b-paid'
                            : 'b-grey'
                        )
                      }
                    >
                      {p.payment_status ===
                        'paid'
                        ? 'Paid'
                        : 'Awaiting Payment'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => loadEntry(p.id)}>View Payslip</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewEntry && <PayslipModal entry={viewEntry} onClose={() => setViewEntry(null)} />}
    </div>
  );
}

function PayslipModal({ entry, onClose }) {
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const additions = (entry.items||[]).filter(i=>i.type==='addition');
  const deductions = (entry.items||[]).filter(i=>i.type==='deduction');

  const printPayslip = () => {
    const w = window.open('', '_blank');
    const addRows = additions.map(i => `
      <tr><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8">${i.label}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;text-align:right;color:#2E6B3A;font-family:serif">+${fmt(i.amount)}</td></tr>`).join('');
    const dedRows = deductions.map(i => `
      <tr><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8">${i.label}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;text-align:right;color:#8F2E24;font-family:serif">-${fmt(i.amount)}</td></tr>`).join('');

    w.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"><title>Payslip - ${entry.staff_name}</title>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
      <style>body{font-family:Inter,sans-serif;margin:0;padding:20px}@media print{body{margin:0;padding:0}}</style>
      </head><body>
      <div style="max-width:480px;margin:0 auto;border:1px solid #EDE7D8;border-radius:10px;overflow:hidden">
        <div style="background:#556B2F;padding:18px 24px;display:flex;justify-content:space-between;align-items:center">
          <div>
            <div style="font-family:Cormorant Garamond,serif;font-size:1.3rem;font-weight:600;color:#A8893D">BLOANS CREATIONS LTD</div>
            <div style="font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:rgba(242,239,231,.5);margin-top:2px">Payslip</div>
          </div>
          <div style="text-align:right;font-size:12px;color:rgba(242,239,231,.6)">
            <div style="font-family:Cormorant Garamond,serif;font-size:1.1rem;color:#A8893D">${MONTHS[(entry.month||1)-1]} ${entry.year}</div>
          </div>
        </div>
        <div style="padding:16px 24px;background:#F2EFE7;border-bottom:1px solid #DDD4C0">
          <div style="font-weight:600;font-size:15px;color:#3B2A21">${entry.staff_name}</div>
          <div style="font-size:12px;color:#555;margin-top:3px">${entry.staff_role||''} ${entry.department?'| '+entry.department:''}</div>
          ${entry.bank_name?`<div style="font-size:11px;color:#8A8A8A;margin-top:3px">${entry.bank_name} | ${entry.bank_account_no} | ${entry.bank_account_name}</div>`:''}
        </div>
        <div style="padding:16px 24px">
          ${entry.payment_status !== 'paid' ? `
            <div style="background:#FFF3DC;color:#8B5E0A;border:1px solid #E4C27A;border-radius:7px;padding:9px 11px;margin-bottom:12px;font-size:11px;font-weight:600;text-align:center">
              DRAFT PAYSLIP — AWAITING PAYMENT
            </div>
          ` : ''}
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <tr style="background:#f9f9f9">
              <td style="padding:8px 12px;border-bottom:1px solid #EDE7D8;font-weight:500">Base Pay</td>
              <td style="padding:8px 12px;border-bottom:1px solid #EDE7D8;text-align:right;font-family:serif;font-size:14px">${fmt(entry.base_pay)}</td>
            </tr>
            ${entry.days_worked?`<tr><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;color:#8A8A8A;font-size:12px">Days Worked</td><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;text-align:right;font-size:12px">${entry.days_worked} days</td></tr>`:''}
            ${addRows}
            ${dedRows}
          </table>
          <div style="margin-top:12px;padding:14px;background:#F2EFE7;border-radius:8px">
            <div style="display:flex;justify-content:space-between;margin-bottom:6px;font-size:13px">
              <span style="color:#8A8A8A">Gross Pay</span><span style="font-family:serif">${fmt(entry.gross_pay)}</span></div>
            <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:13px">
              <span style="color:#8A8A8A">Total Deductions</span><span style="font-family:serif;color:#8F2E24">-${fmt(entry.total_deductions)}</span></div>
            <div style="display:flex;justify-content:space-between;padding-top:8px;border-top:1px solid #DDD4C0">
              <span style="font-weight:700;font-size:14px;color:#3B2A21">NET PAY</span>
              <span style="font-family:Cormorant Garamond,serif;font-size:1.5rem;font-weight:700;color:#556B2F">${fmt(entry.net_pay)}</span>
            </div>
          </div>
          ${entry.notes?`<div style="margin-top:10px;font-size:11px;color:#8A8A8A">Notes: ${entry.notes}</div>`:''}
        </div>
        <div style="padding:10px 24px;background:#556B2F;text-align:center">
          <div style="font-size:10px;color:rgba(242,239,231,.5)">This is a computer generated payslip — Bloans Creations Ltd</div>
        </div>
      </div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-hdr">
          <span className="modal-title">Payslip — {MONTHS[(entry.month||1)-1]} {entry.year}</span>
          <div className="flex-center gap-sm">
            <button className="btn btn-primary btn-sm" onClick={printPayslip}>Print / Download</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          {entry.payment_status !== 'paid' && (
            <div style={{
              background:'#FFF3DC',
              color:'#8B5E0A',
              border:'1px solid #E4C27A',
              borderRadius:8,
              padding:'10px 12px',
              marginBottom:14,
              fontSize:12,
              fontWeight:600
            }}>
              Draft payslip for review.
              Payment has not yet been made.
              Figures remain subject to final approval.
            </div>
          )}
          <div style={{background:'#F2EFE7',borderRadius:8,padding:14,marginBottom:14}}>
            <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{entry.staff_name}</div>
            <div style={{fontSize:12,color:'#555',marginTop:2}}>{entry.staff_role||''}</div>
            {entry.days_worked && <div style={{fontSize:12,color:'#8A8A8A',marginTop:4}}>Days worked: {entry.days_worked}</div>}
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13,marginBottom:14}}>
            <tbody>
              <tr style={{background:'#f9f9f9'}}>
                <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>Base Pay</td>
                <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(entry.base_pay)}</td>
              </tr>
              {additions.map((item,i) => (
                <tr key={i}>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',color:'var(--paid)'}}>{item.label}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',color:'var(--paid)',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>+{fmt(item.amount)}</td>
                </tr>
              ))}
              {deductions.map((item,i) => (
                <tr key={i}>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',color:'var(--unpaid)'}}>{item.label}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',color:'var(--unpaid)',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>-{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{background:'#F2EFE7',borderRadius:8,padding:14}}>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
              <span style={{color:'#8A8A8A'}}>Gross Pay</span><span>{fmt(entry.gross_pay)}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
              <span style={{color:'#8A8A8A'}}>Total Deductions</span>
              <span style={{color:'var(--unpaid)'}}>-{fmt(entry.total_deductions)}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #DDD4C0'}}>
              <span style={{fontWeight:700,fontSize:14,color:'#3B2A21'}}>NET PAY</span>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#556B2F'}}>{fmt(entry.net_pay)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
